import { Children, PropsWithChildren, forwardRef, isValidElement, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { AccessibilityProps } from "capitalrx-components-2.0/lib/AccessibilityProps";
import { BaseProps } from "capitalrx-components-2.0/lib/BaseProps";
import { RefComponent } from "capitalrx-components-2.0/lib/forms";
import { Spacing } from "capitalrx-components-2.0/lib/layout";
import { useResponsive } from "capitalrx-components-2.0/lib/layout/hooks/useResponsive";
import { ResponsiveDeclaration } from "capitalrx-components-2.0/lib/layout/hooks/useResponsive/ResponsiveDeclaration";
import { getHtmlPropsFromBaseProps } from "capitalrx-components-2.0/lib/utilities/getHtmlPropsFromBaseProps";
import { withSubComponents } from "capitalrx-components-2.0/lib/utilities/withSubComponents";
import classNames from "classnames";

import { HorizontalAlignment, ShelfAlignment, VerticalAlignment } from "./ShelfAlignment";
import { Spacer } from "./Spacer";

import styles from "./styles.module.scss";

/** Base shelf component properties. */
interface BaseShelfProps extends BaseProps, AccessibilityProps {
  /** Specifies the vertical alignment of the elements within the shelf. If not specified defaults to `"center"`. */
  readonly alignment?: ShelfAlignment;

  /** Indicates whether the shelf should render children in reverse order. If not specified, defaults to `false` */
  readonly reverse?: boolean;

  /** The spacing between items. If not specified defaults to "medium". */
  readonly spacing?: Spacing;

  /** Indicates whether to render the shelf as inline. */
  readonly inline?: boolean;

  /**
   * Automatically adjusts spacing based on container width for better responsive design.
   * When enabled, uses tighter spacing on smaller containers and looser on larger ones.
   * - `true`: Auto-adjusts spacing based on container size
   * - `false`: Uses fixed spacing from `spacing` prop (default)
   */
  readonly adaptiveSpacing?: boolean;
}

/** Simplified error messages for better developer experience */
type INVALID_STACK_IN_WRAP_MODE = "'stack' not allowed in wrap mode";
type INVALID_WRAP_IN_STACK_MODE = "'wrap' not allowed in stack mode ";
type INVALID_WRAP_IN_DEFAULT_MODE = "'wrap' requires responsive=true";
type INVALID_STACK_IN_DEFAULT_MODE = "'stack' requires responsive=true";

/** Wrap mode alignment configuration */
type WrapModeAlignmentConfig = {
  readonly default?: VerticalAlignment;
  readonly wrap?: HorizontalAlignment;
  readonly stack?: INVALID_STACK_IN_WRAP_MODE;
};

/** Stack mode alignment configuration */
type StackModeAlignmentConfig = {
  readonly default?: VerticalAlignment;
  readonly stack?: HorizontalAlignment;
  readonly wrap?: INVALID_WRAP_IN_STACK_MODE;
};

/** Default mode alignment configuration */
type DefaultModeAlignmentConfig = {
  readonly default?: VerticalAlignment;
  readonly wrap?: INVALID_WRAP_IN_DEFAULT_MODE;
  readonly stack?: INVALID_STACK_IN_DEFAULT_MODE;
};

type ScreenResponsive = {
  readonly responsive: "screen";
  readonly responsiveAlignment?: never;
  readonly responsiveOptions?: Pick<
    ResponsiveDeclaration<{
      readonly stack: boolean;
      readonly alignment?: HorizontalAlignment;
    }>,
    "tablet" | "mobile"
  >;
};
/** The shelf component properties with comprehensive type-safe responsive alignment */
export type ShelfProps = BaseShelfProps
  & (
    | {
        readonly responsive: "wrap";
        readonly responsiveAlignment?: WrapModeAlignmentConfig | VerticalAlignment | HorizontalAlignment;
      }
    | {
        readonly responsive: true | (Exclude<string, "wrap" | "screen"> & {});
        readonly responsiveAlignment?: StackModeAlignmentConfig | VerticalAlignment | HorizontalAlignment;
      }
    | {
        readonly responsive?: false | undefined;
        readonly responsiveAlignment?: DefaultModeAlignmentConfig | VerticalAlignment;
      }
    | ScreenResponsive
  );

/** The `Shelf` component subcomponents. */
export interface ShelfSubComponents {
  /** A component which pushes content in a shelf to the left and right. */
  readonly Spacer: typeof Spacer;
}

/**
 * Horizontally places child elements next to each other with type-safe responsive alignment.
 *
 * TypeScript will show descriptive error messages when invalid alignment combinations are used:
 * - Wrap mode (responsive="wrap"): Only 'default' and 'wrap' properties allowed
 * - Stack mode (responsive=true): Only 'default' and 'stack' properties allowed
 * - Screen mode (responsive="screen"): Uses screen-size breakpoints (mobile/tablet/smallDesktop/desktop), no responsiveAlignment needed, alignment prop used instead and applied for desktop size only, other sizes uses 100% width stacking
 * - Default mode: Only 'default' property allowed
 */
export const Shelf = withSubComponents<RefComponent<HTMLDivElement, PropsWithChildren<ShelfProps>>, ShelfSubComponents>(
  {
    Spacer,
  },
  forwardRef<HTMLDivElement, PropsWithChildren<ShelfProps>>((props, ref) => {
    const {
      alignment = "center",
      spacing = "medium",
      inline = false,
      reverse = false,
      responsive = false,
      responsiveAlignment,
      adaptiveSpacing = false,
      children,
      role,
    } = props;

    // Enhanced responsive prop handling with validation
    const isResponsive = !!responsive && responsive !== "wrap";
    const isWrap = responsive === "wrap";
    const isScreen = responsive === "screen";
    const customBreakpoint = useMemo(() => {
      if (typeof responsive === "string" && responsive !== "wrap" && responsive !== "screen") {
        const breakpoint = parseInt(responsive);
        if (isNaN(breakpoint) || breakpoint <= 0) {
          console.warn(
            `Shelf: Invalid responsive breakpoint "${responsive}". Expected positive number or "wrap". Falling back to default (768px).`,
          );
          return null;
        }
        return responsive;
      }
      return null;
    }, [responsive]);

    const toVerticalAlignment = (a: ShelfAlignment): VerticalAlignment => {
      return a === "top" || a === "center" || a === "bottom" ? a : "center";
    };

    const responsiveOptions =
      "responsiveOptions" in props && props.responsiveOptions != null
        ? props.responsiveOptions
        : {
            tablet: {
              stack: true,
              alignment: "stretch",
            },
            mobile: {
              stack: true,
              alignment: "stretch",
            },
          };

    /* When responsive === "screen", compute alignment and narrow state via useResponsive
       Mobile uses stack with center alignment others uses alignment prop*/
    const screenAlignmentDeclaration: ResponsiveDeclaration<VerticalAlignment | unknown> = useMemo(() => {
      const tabletAlignment = responsiveOptions.tablet?.stack
        ? (responsiveOptions.tablet?.alignment ?? "stretch")
        : toVerticalAlignment(alignment);
      const mobileAlignment = responsiveOptions.mobile?.stack
        ? (responsiveOptions.mobile?.alignment ?? "stretch")
        : toVerticalAlignment(alignment);
      return {
        desktop: isScreen ? toVerticalAlignment(alignment) : "center",
        smallDesktop: isScreen ? toVerticalAlignment(alignment) : "center",
        tablet: isScreen && tabletAlignment,
        mobile: isScreen && mobileAlignment,
      };
    }, [isScreen, alignment, responsiveOptions]);

    // Always call useResponsive unconditionally (Rules of Hooks)
    const screenResponsiveValues = useResponsive({
      alignment: screenAlignmentDeclaration,
      narrow: {
        desktop: false,
        smallDesktop: false,
        tablet: responsiveOptions.tablet?.stack ?? false,
        mobile: responsiveOptions.mobile?.stack ?? false,
      },
    });

    // Only use the values when in screen mode
    const screenValues = isScreen ? screenResponsiveValues : null;

    // Enhanced alignment resolver with intuitive defaults for different responsive states
    const getAlignment = useCallback(
      (state: "default" | "wrap" | "stack") => {
        // In screen mode, alignment is derived from display mode
        if (isScreen) {
          return screenValues?.alignment ?? alignment;
        }

        if (!responsiveAlignment) return alignment;

        if (typeof responsiveAlignment === "string") return responsiveAlignment;

        // Handle conditional responsive alignment configuration
        const config = responsiveAlignment as WrapModeAlignmentConfig | StackModeAlignmentConfig | DefaultModeAlignmentConfig;
        const defaultAlignment = "default" in config && config.default ? config.default : alignment;

        switch (state) {
          case "default":
            // Default horizontal layout - use vertical alignment (top/center/bottom)
            return defaultAlignment;
          case "wrap":
            /* Wrapped layout - use horizontal alignment (left/center/right) with intuitive default
            Only available when responsive="wrap" */
            return config.wrap || "left"; // Default to left alignment for wrapped content (most natural)
          case "stack":
            /* Stacked layout - use horizontal alignment (left/center/right) with intuitive default
             Only available when responsive={true|"600px"} */
            return config.stack || "center"; // Default to center alignment for stacked content (most balanced)
          default:
            return defaultAlignment;
        }
      },
      [isScreen, screenValues, responsiveAlignment, alignment],
    );

    // ResizeObserver-based responsive behavior with wrap detection
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [isNarrow, setIsNarrow] = useState(false);
    const [hasWrapped, setHasWrapped] = useState(false);
    const [containerWidth, setContainerWidth] = useState(0);

    // Adaptive spacing calculation based on container width
    const currentSpacing = useMemo(() => {
      if (!adaptiveSpacing || containerWidth === 0) return spacing;

      // Define adaptive spacing thresholds using valid Spacing values
      if (containerWidth < 400) return "none";
      if (containerWidth < 600) return "mini";
      if (containerWidth < 900) return "medium";
      if (containerWidth < 1200) return "medium-large";
      return "large";
    }, [adaptiveSpacing, containerWidth, spacing]);

    // Enhanced wrap detection for better responsive behavior
    const checkForWrapping = useCallback(
      (container: HTMLDivElement) => {
        if (!isWrap) return false;

        try {
          // For wrap mode, we need to check the inner flex container, not the outer container
          const flexContainer = container.firstElementChild as HTMLElement;
          if (!flexContainer) return false;

          const children = Array.from(flexContainer.children) as HTMLElement[];
          if (children.length <= 1) return false;

          // Check if any child has wrapped to a new line
          const firstChildTop = children[0]?.getBoundingClientRect().top;
          const hasWrappedContent = children.some((child, index) => {
            if (index === 0) return false;
            const childTop = child.getBoundingClientRect().top;
            return Math.abs(childTop - firstChildTop) > 5; // 5px tolerance for alignment
          });

          return hasWrappedContent;
        } catch (error) {
          console.error("Shelf: Wrap detection error:", error);
          return false;
        }
      },
      [isWrap],
    );

    // Optimized ResizeObserver effect with improved performance
    useEffect(() => {
      if ((!isResponsive && !isWrap) || typeof window === "undefined" || !window.ResizeObserver) return;

      const container = containerRef.current;
      if (!container) return;

      const breakpoint = customBreakpoint ? parseInt(customBreakpoint) : 768;

      // Validate breakpoint for responsive mode only
      if (isResponsive && (isNaN(breakpoint) || breakpoint <= 0)) {
        console.warn("Shelf: Invalid responsive breakpoint. Expected positive number.");
        return;
      }

      // Use requestAnimationFrame for better performance than setTimeout
      let rafId: number;
      let isScheduled = false;

      const scheduleUpdate = (callback: () => void) => {
        if (!isScheduled) {
          isScheduled = true;
          rafId = requestAnimationFrame(() => {
            isScheduled = false;
            callback();
          });
        }
      };

      const resizeObserver = new ResizeObserver((entries) => {
        scheduleUpdate(() => {
          try {
            for (const entry of entries) {
              const { width } = entry.contentRect;
              const container = entry.target as HTMLDivElement;

              // Batch state updates to prevent unnecessary re-renders
              const newNarrowState = isResponsive ? width < breakpoint : false;
              const newWrappedState = isWrap ? checkForWrapping(container) : false;

              // Only update if state actually changed
              if (isResponsive) {
                setIsNarrow((prev) => (prev !== newNarrowState ? newNarrowState : prev));
              }
              if (isWrap) {
                setHasWrapped((prev) => (prev !== newWrappedState ? newWrappedState : prev));
              }

              // Update container width for adaptive spacing
              if (adaptiveSpacing) {
                setContainerWidth((prev) => (prev !== width ? width : prev));
              }
            }
          } catch (error) {
            console.error("Shelf: ResizeObserver callback error:", error);
            // Graceful degradation with error recovery
            setIsNarrow(false);
            setHasWrapped(false);
            setContainerWidth(0);
          }
        });
      });

      resizeObserver.observe(container);

      // Initial state setup with error handling
      scheduleUpdate(() => {
        try {
          const { width } = container.getBoundingClientRect();
          const initialNarrow = isResponsive ? width < breakpoint : false;
          const initialWrapped = isWrap ? checkForWrapping(container) : false;

          if (isResponsive) setIsNarrow(initialNarrow);
          if (isWrap) setHasWrapped(initialWrapped);
          if (adaptiveSpacing) setContainerWidth(width);
        } catch (error) {
          console.error("Shelf: Initial size check error:", error);
          setIsNarrow(false);
          setHasWrapped(false);
          setContainerWidth(0);
        }
      });

      return () => {
        if (rafId) cancelAnimationFrame(rafId);
        resizeObserver.disconnect();
      };
    }, [isResponsive, isWrap, customBreakpoint, adaptiveSpacing, checkForWrapping]);

    // Additional MutationObserver for wrap detection when children change
    useEffect(() => {
      if (!isWrap || typeof window === "undefined") return;

      const container = containerRef.current;
      if (!container) return;

      // Check for MutationObserver support with graceful degradation
      if (!window.MutationObserver) {
        console.warn("Shelf: MutationObserver not supported. Wrap detection may be limited.");
        return;
      }

      // Throttled mutation handler for better performance
      let mutationTimeoutId: ReturnType<typeof setTimeout>;
      const handleMutations = () => {
        clearTimeout(mutationTimeoutId);
        mutationTimeoutId = setTimeout(() => {
          try {
            const newWrapped = checkForWrapping(container);
            setHasWrapped((prev) => (prev !== newWrapped ? newWrapped : prev));
          } catch (error) {
            console.error("Shelf: Mutation wrap check failed:", error);
            // Don't reset state on error, keep last known state
          }
        }, 100); // Reasonable delay for DOM settling
      };

      const mutationObserver = new MutationObserver(handleMutations);

      // Observe with refined configuration for better performance
      mutationObserver.observe(container, {
        childList: true,
        subtree: false, // Only observe direct children changes
        attributes: false,
        characterData: false,
      });

      return () => {
        clearTimeout(mutationTimeoutId);
        mutationObserver.disconnect();
      };
    }, [isWrap, checkForWrapping, children]);

    // Enhanced current alignment calculation with proper wrap detection
    const currentAlignment = useMemo(() => {
      if (isScreen) return getAlignment("default");
      if (isWrap && hasWrapped) return getAlignment("wrap");
      if (isResponsive && isNarrow) return getAlignment("stack");
      return getAlignment("default");
    }, [isScreen, isWrap, hasWrapped, isResponsive, isNarrow, getAlignment]);

    // Determine if we should show narrow/stacked mode
    const shouldBeNarrow = useMemo(() => {
      if (isScreen) return screenValues?.narrow ?? false;
      if (isResponsive) return isNarrow;
      return false;
    }, [isScreen, screenValues, isResponsive, isNarrow]);

    /* This merges containerRef for the ResizeObserver with a forwarded ref for consumers to ensure both
       the component's internal ref and parent ref point to the same node. */
    const handleRef = useCallback(
      (node: HTMLDivElement | null) => {
        containerRef.current = node;
        if (typeof ref === "function") {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      },
      [ref],
    );

    const baseHtmlProps = getHtmlPropsFromBaseProps(props, {
      className: [
        styles.Shelf,
        "shelf",
        `align-${currentAlignment}`,
        `space-${currentSpacing}`, // Use adaptive spacing instead of fixed spacing
        {
          inline,
          reverse,
          responsive: isResponsive || isScreen,
          wrap: isWrap,
          narrow: shouldBeNarrow, // Add class when narrow (from screen mode or breakpoint mode)
          adaptive: adaptiveSpacing, // Add class for adaptive spacing styles
          screen: isScreen, // Add class for screen mode
        },
      ],
      testIdPrefix: "shelf",
    });

    // Merge styles manually to handle responsive properties
    const mergedStyle =
      isResponsive && customBreakpoint
        ? ({
            ...baseHtmlProps.style,
            "--shelf-responsive-breakpoint": customBreakpoint,
          } as React.CSSProperties)
        : baseHtmlProps.style;

    // If responsive or screen mode, wrap in a container div
    if (isResponsive || isWrap || isScreen) {
      return (
        <div {...baseHtmlProps} style={mergedStyle} ref={handleRef}>
          {Children.map(children, (child) => {
            if (!isValidElement(child) || child.type !== Spacer) return child;
            return <div className={classNames(styles.Spacer, "spacer")} />;
          })}
        </div>
      );
    }

    // Non-responsive version
    return (
      <div
        {...baseHtmlProps}
        style={mergedStyle}
        ref={ref}
        role={role}
        aria-label={props["aria-label"]}
        aria-labelledby={props["aria-labelledby"]}
      >
        {Children.map(children, (child) => {
          if (!isValidElement(child) || child.type !== Spacer) return child;

          return <div className={classNames(styles.Spacer, "spacer")} />;
        })}
      </div>
    );
  }),
);
